import { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import TiltCard from "./TiltCard";
import { usePortfolioProjects } from "@/hooks/usePortfolioProjects";
import { Skeleton } from "@/components/ui/skeleton";
import { isVideoUrl } from "@/lib/mediaUtils";

const Portfolio = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { projects, loading } = usePortfolioProjects({ featuredOnly: true, limit: 4 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="work"
      ref={sectionRef}
      className="py-10 md:py-14 relative bg-card/30"
    >
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/50 to-background pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-6 md:mb-8">
          <p
            className={`text-primary font-medium tracking-widest uppercase text-xs mb-2 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Portfolio
          </p>
          <h2
            className={`text-2xl md:text-4xl lg:text-5xl font-black mb-3 ${
              isVisible ? "animate-cinematic-fade-up" : "opacity-0"
            }`}
            style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
          >
            My Portfolio: Real Results for <span className="text-gradient-static">Local Clients</span>
          </h2>
          <p
            className={`text-xs md:text-base text-muted-foreground max-w-xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Custom websites crafted for Hamptons businesses, real estate agents, contractors, and local brands
          </p>
        </div>

        {/* Projects Grid - Show only featured (first 4) */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-3xl mx-auto">
          {loading ? (
            // Loading skeletons
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl overflow-hidden">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="aspect-[4/3] w-full" />
              </div>
            ))
          ) : (
            projects.map((project, index) => (
              <TiltCard
                key={project.id}
                className={`transition-all duration-700 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
                }`}
                tiltMaxAngle={12}
              >
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block overflow-hidden rounded-xl bg-secondary/50 shadow-lg transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_hsl(var(--primary)/0.3)]"
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  {/* Animated Rainbow Border - Always visible */}
                  <div className="absolute -inset-[2px] rounded-xl opacity-100 transition-opacity duration-500 pointer-events-none z-10 rainbow-border" />
                  <div className="absolute inset-0 rounded-xl bg-card z-10 pointer-events-none" style={{ margin: '2px' }} />

                  {/* Title Above Thumbnail */}
                  <div className="relative z-20 px-2 py-2 sm:px-5 sm:py-4 bg-card/95 border-b border-border/50 rounded-t-xl">
                    <h3 className="text-xs sm:text-lg md:text-xl lg:text-2xl font-semibold text-primary">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground/80 text-[10px] sm:text-sm md:text-base">
                      {project.description}
                    </p>
                  </div>

                  {/* Thumbnail Container */}
                  <div className="relative aspect-[4/3] overflow-hidden z-20 flex items-center justify-center bg-secondary/30">
                    {project.video_url ? (
                      <div className="relative w-[70%] h-[70%] group/media">
                        <img
                          src={project.image_url || "/placeholder.svg"}
                          alt={`${project.title} thumbnail`}
                          width={300}
                          height={200}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover rounded-lg opacity-85 scale-110 brightness-110 transition-all duration-700 ease-out group-hover:scale-125 absolute inset-0 group-hover/media:opacity-0"
                        />
                        <video
                          src={project.video_url}
                          className="w-full h-full object-cover rounded-lg opacity-0 group-hover/media:opacity-85 scale-110 brightness-110 transition-all duration-700 ease-out group-hover:scale-125"
                          muted
                          loop
                          playsInline
                          onMouseEnter={(e) => e.currentTarget.play()}
                          onMouseLeave={(e) => {
                            e.currentTarget.pause();
                            e.currentTarget.currentTime = 0;
                          }}
                        />
                      </div>
                    ) : (
                      <img
                        src={project.image_url || "/placeholder.svg"}
                        alt={`Custom website design for ${project.title}`}
                        width={300}
                        height={200}
                        loading="lazy"
                        decoding="async"
                        className="w-[70%] h-[70%] object-cover rounded-lg opacity-85 scale-110 brightness-110 transition-all duration-700 ease-out group-hover:scale-125"
                      />
                    )}

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-6">
                      <div className="flex items-center gap-2 text-primary font-medium bg-card/80 backdrop-blur-sm px-4 py-2 rounded-full border border-primary/30 shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                        <span>Visit Website</span>
                        <ExternalLink
                          size={16}
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </div>
                    </div>
                  </div>
                </a>
              </TiltCard>
            ))
          )}
        </div>

        {/* View All Button */}
        <div
          className={`text-center mt-6 transition-all duration-700 delay-[800ms] ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm border border-primary text-primary font-semibold tracking-wider uppercase transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:shadow-[0_0_30px_hsl(var(--primary)/0.4)] hover:scale-105"
          >
            View All Projects
            <ExternalLink size={14} />
          </Link>
        </div>

        {/* Call to Action */}
        <div
          className={`text-center mt-4 transition-all duration-700 delay-[900ms] ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-muted-foreground text-sm mb-3">
            Want to see your business here?
          </p>
          <a
            href="sms:+16317070672?body=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm bg-primary text-primary-foreground font-semibold tracking-wider uppercase transition-all duration-300 hover:shadow-[0_0_30px_hsl(var(--primary)/0.4)] hover:scale-105"
          >
            Start Your Project
          </a>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
